import React, { useState } from 'react';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { ADALONG_COLORS } from '@src/styles';

import { useSettings } from './hooks/useSettings';
import { FormButtons } from '@src/components/Form/FormButtons';
import { ActionButton } from '@src/components/ActionButton';
import { PerfectScrollbar } from 'components/PerfectScrollbar';
import { SettingsProps } from './types/Settings';
import { style } from 'typestyle';
import { SectionProgressLoader } from '@src/components/SectionProgressLoader';
import { Error } from '../../Error';

import Information from './components/Information';
import QuickDesign from './components/QuickDesign';
import Layout from './components/Layout';
import ShopThisLook from './components/ShopThisLook';
import QuickShopThisLook from './components/QuickShopThisLook';
import ProductDisplay from './components/ProductDisplay';
import ThumbnailDesign from './components/ThumbnailDesign';
import QuickProducts from './components/QuickProducts';
import ContentFilters from './components/ContentFilters';
import MobilePlayer from './components/MobilePlayer';
import Attribution from './components/Attribution';
import QuickType from './components/QuickType';
import LocalCatalog from './components/LocalCatalog';
import ProductCollectionFilter from './components/ProductCollectionFilter';

export function Settings({
  remoteWidget,
  editedWidget,
  setEditedWidget,
  productCountryMappings,
  setCurrentLocalization,
}: SettingsProps) {
  const {
    sectionOpened,
    setSectionOpened,
    onResetClick,
    onSaveClick,
    isLoading,
    error,
    disableSubmit,
    goToAdvancedProductSettings,
  } = useSettings({ remoteWidget, editedWidget, setEditedWidget, setCurrentLocalization });

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const toggleAdvancedSettings = () => {
    setShowAdvancedSettings(!showAdvancedSettings);
  };

  return (
    <PerfectScrollbar>
      <div className={settingsClass}>
        <Information
          opened={sectionOpened.info}
          onOpen={(info) => setSectionOpened({ ...sectionOpened, info })}
          editedWidget={editedWidget}
          setEditedWidget={setEditedWidget}
        />

        {!showAdvancedSettings ? (
          <>
            <QuickDesign
              opened={sectionOpened.quickDesign}
              onOpen={(quickDesign) => setSectionOpened({ ...sectionOpened, quickDesign })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
              onGoToAdvancedSettings={() => {
                setShowAdvancedSettings(true);
                setSectionOpened({ ...sectionOpened, layout: true });
              }}
            />

            <QuickShopThisLook
              opened={sectionOpened.quickShopThisLook}
              onOpen={(quickShopThisLook) => setSectionOpened({ ...sectionOpened, quickShopThisLook })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            {/* Only show QuickProducts section when shop_this_look_display is not 'none' */}
            {editedWidget.settings.shop_this_look_display !== 'none' && (
              <QuickProducts
                key={`quick-products-${editedWidget.settings.has_exact_products}-${editedWidget.settings.has_similar_products}`}
                opened={sectionOpened.quickProducts}
                onOpen={(quickProducts) => setSectionOpened({ ...sectionOpened, quickProducts })}
                editedWidget={editedWidget}
                setEditedWidget={setEditedWidget}
                currentLayout={editedWidget.settings.type || 'wall'}
                carouselType={editedWidget.settings.carousel_type}
                onGoToAdvancedSettings={() => {
                  setShowAdvancedSettings(true);
                  goToAdvancedProductSettings();
                }}
              />
            )}

            <QuickType
              opened={sectionOpened.quickType}
              onOpen={(quickType) => setSectionOpened({ ...sectionOpened, quickType })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
              remoteWidget={remoteWidget}
            />
          </>
        ) : null}

        {!showAdvancedSettings ? (
          <div className={advancedSettingsButtonClass}>
            <ActionButton variant='gray' onAction={toggleAdvancedSettings}>
              {lang.showAdvancedSettings.toUpperCase()}
            </ActionButton>
          </div>
        ) : null }

        {showAdvancedSettings ? (
          <div className={advancedSettingsButtonClass}>
            <ActionButton variant='gray' onAction={toggleAdvancedSettings}>
              {lang.showQuickSettings.toUpperCase()}
            </ActionButton>
          </div>
        ) : null}


        <div className={separatorLineClass}></div>

        {showAdvancedSettings ? (
          <>
            <Layout
              opened={sectionOpened.layout}
              onOpen={(layout) => setSectionOpened({ ...sectionOpened, layout })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            <ThumbnailDesign
              opened={sectionOpened.thumbnailDesign}
              onOpen={(thumbnailDesign) => setSectionOpened({ ...sectionOpened, thumbnailDesign })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            <ContentFilters
              opened={sectionOpened.contentFilters}
              onOpen={(contentFilters) => setSectionOpened({ ...sectionOpened, contentFilters })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            <ProductCollectionFilter
              opened={sectionOpened.productCollectionFilter}
              onOpen={(productCollectionFilter) => setSectionOpened({ ...sectionOpened, productCollectionFilter })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            <ShopThisLook
              opened={sectionOpened.shopThisLook}
              onOpen={(shopThisLook) => setSectionOpened({ ...sectionOpened, shopThisLook })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            <LocalCatalog
              opened={sectionOpened.localCatalog}
              onOpen={(localCatalog) => setSectionOpened({ ...sectionOpened, localCatalog })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
              productCountryMappings={productCountryMappings}
              setCurrentLocalCatalog={setCurrentLocalization}
            />

            {editedWidget.settings.shop_this_look_display !== 'none' ? (
              <ProductDisplay
                opened={sectionOpened.productDisplay}
                onOpen={(productDisplay) => setSectionOpened({ ...sectionOpened, productDisplay })}
                editedWidget={editedWidget}
                setEditedWidget={setEditedWidget}
              />
            ) : null }

            <MobilePlayer
              opened={sectionOpened.mobilePlayer}
              onOpen={(mobilePlayer) => setSectionOpened({ ...sectionOpened, mobilePlayer })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />

            <Attribution
              opened={sectionOpened.attribution}
              onOpen={(attribution) => setSectionOpened({ ...sectionOpened, attribution })}
              editedWidget={editedWidget}
              setEditedWidget={setEditedWidget}
            />
          </>
        ) : null}

        {error ? <Error error={error} /> : null}

        <FormButtons className={buttonsClass}>
          <ActionButton 
            variant='transparent' 
            onAction={onResetClick}
            className={buttonClass}
          >
            {lang.reset.toUpperCase()}
          </ActionButton>
          <ActionButton 
            variant='blue' 
            onAction={onSaveClick} 
            disabled={disableSubmit}
            className={buttonClass}
          >
            {lang.save.toUpperCase()}
          </ActionButton>
        </FormButtons>

        {isLoading ? <SectionProgressLoader indeterminate opacity={0.6} /> : null}
      </div>
    </PerfectScrollbar>
  );
}

const settingsClass = style({
  padding: '10px 20px 30px 0',
  position: 'relative',
  width: 326,
});

const buttonsClass = style({
  $debugName: 'buttons',
  margin: '0 auto',
  width: '80%',
  gap: '20px',
});

const buttonClass = style({
  $debugName: 'button',
  flex: 1,
  minWidth: 0,
});

const advancedSettingsButtonClass = style({
  $debugName: 'advancedSettingsButton',
  display: 'flex',
  justifyContent: 'center',
  margin: '20px 0',
});

const separatorLineClass = style({
  $debugName: 'separatorLine',
  height: '1px',
  width: '100%',
  backgroundColor: ADALONG_COLORS.DARKER_GRAY,
  margin: '20px 0',
});

const lang = LocalizedStrings({
  en: {
    save: 'save',
    reset: 'reset',
    showAdvancedSettings: 'Show Advanced Settings',
    hideAdvancedSettings: 'Hide Advanced Settings',
    showQuickSettings: 'Show Quick Settings',
  },
});
